import {assert} from 'Asserts';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import DocumentStore from 'notebook/documents/DocumentStore';
import {normalizeNameForUniqueness} from 'procedures/design/flowchartManagementUtils';
import {getMaterialCidFromHandle} from 'procedures/design/handleIdentifierUtils';
import {getMethodNamePlaceholder} from 'procedures/design/procedureMethodNodeCommonConsts';
import {
  DataParameterTypeREST,
  EquipmentTypeREST,
  MaterialInputTypeREST,
  MaterialOutputTypeREST,
  MethodNodeREST,
  ProcedureAnalysisTemplateConfigurationREST,
  ProcedureDataParameterPlannedValueREST,
  ProcedureDataParameterSettingREST,
  ProcedureDataParameterVersionREST,
  ProcedureEdgeREST,
  ProcedureEquipmentPlannedValueREST,
  ProcedureEquipmentSettingREST,
  ProcedureEquipmentVersionREST,
  ProcedureFlowchartREST,
  ProcedureMaterialInputPlannedValueREST,
  ProcedureMaterialInputSettingREST,
  ProcedureMaterialInputVersionREST,
  ProcedureMaterialOutputSettingREST,
  ProcedureMaterialOutputVersionREST,
  ProcedureStepDefinitionVersionREST,
  ProcedureStepGroupREST,
  ProcedureTestDefinitionREST,
} from 'procedures/network/ProcedureDAO';
import {
  InaccessibleProcedureAnalysisTemplateConfiguration,
  InaccessibleProcedureTestDefinition,
  isInaccessibleAnalysisTemplateConfiguration,
  isInaccessibleProcedureTestDefinition,
  MethodNode,
  ProcedureAnalysisTemplateConfiguration,
  ProcedureDataParameterPlannedValue,
  ProcedureDataParameterSetting,
  ProcedureDataParameterVersion,
  ProcedureEdge,
  ProcedureEdgeType,
  ProcedureEquipmentPlannedValue,
  ProcedureEquipmentSetting,
  ProcedureEquipmentVersion,
  ProcedureFlowchart,
  ProcedureMaterialInputPlannedValue,
  ProcedureMaterialInputSetting,
  ProcedureMaterialInputVersion,
  ProcedureMaterialOutputSetting,
  ProcedureMaterialOutputVersion,
  ProcedureStepConfig,
  ProcedureStepGroup,
  ProcedureTestDefinition,
} from 'procedures/ProcedureDomainTypes';
import {PersistenceInfo} from 'procedures/ProcedurePageContext';
import {
  DataParameterType,
  EquipmentType,
  MaterialInputType,
  MaterialOutputType,
  UnitSummary,
} from 'procedures/shared-components/schema-field-type-selectors/procedureSchemaFieldConstants';

/**
 * @package Exported for testing
 */
export function serializeProcedureStepConfigForNetwork(
  stepConfig: ProcedureStepConfig,
  persistenceInfo?: PersistenceInfo
): ProcedureStepDefinitionVersionREST {
  const {id, cid, name, documentId, fromId, documentVersionUpdate} = stepConfig;
  const stepDefinition: ProcedureStepDefinitionVersionREST = {id, cid, name, fromId: fromId ?? undefined};
  if (fromId && documentId != null) {
    stepDefinition.documentVersion = {id: fromId};
    if (documentVersionUpdate) {
      const {toServerDocument} = DocumentStore.getToServerDocument(documentId);
      stepDefinition.documentVersion.jsonData = toServerDocument;
    }
  }
  if (persistenceInfo?.cloneStepCidToDocumentVersionIdMap?.hasOwnProperty(cid)) {
    stepDefinition.fromId = persistenceInfo.cloneStepCidToDocumentVersionIdMap[cid];
  }
  return stepDefinition;
}

function serializeProcedureStepGroupForNetwork(
  stepGroup: ProcedureStepGroup,
  persistenceInfo?: PersistenceInfo
): ProcedureStepGroupREST {
  const {group, steps} = stepGroup;
  return {
    group,
    steps: steps.map((step) => serializeProcedureStepConfigForNetwork(step, persistenceInfo)),
  };
}

function serializeMaterialAmountUnitForNetwork(unit: UnitSummary | null): string | null {
  return unit ? unit.id : null;
}

function serializeMaterialInputTypeForNetwork(materialInputType: MaterialInputType): MaterialInputTypeREST {
  return {
    type: materialInputType.type,
    requiredLink: materialInputType.requiredLink,
    unitId: null,
  };
}

function serializeMaterialOutputTypeForNetwork(
  materialOutputType: MaterialOutputType
): MaterialOutputTypeREST {
  return {
    type: materialOutputType.type,
    requiredLink: materialOutputType.requiredLink,
    unitId: null,
  };
}

function serializeMaterialInputVersionForNetwork(
  materialInputVersion: ProcedureMaterialInputVersion
): ProcedureMaterialInputVersionREST {
  const {id, cid, name, description, clonedFromId, procedureMaterialInputId, confirmationMethod} =
    materialInputVersion;
  return {
    id,
    cid,
    name,
    description,
    clonedFromId,
    procedureMaterialInputId,
    confirmationMethod,
    dataType: serializeMaterialInputTypeForNetwork(materialInputVersion.dataType),
    amountUnitId: serializeMaterialAmountUnitForNetwork(materialInputVersion.amountUnit),
  };
}

function serializeMaterialOutputVersionForNetwork(
  materialOutputVersion: ProcedureMaterialOutputVersion
): ProcedureMaterialOutputVersionREST {
  const {id, cid, name, description, clonedFromId, procedureMaterialOutputId} = materialOutputVersion;
  return {
    id,
    cid,
    name,
    description,
    clonedFromId,
    procedureMaterialOutputId,
    dataType: serializeMaterialOutputTypeForNetwork(materialOutputVersion.dataType),
  };
}

function serializeEquipmentTypeForNetwork(equipmentType: EquipmentType): EquipmentTypeREST {
  return {
    type: equipmentType.type,
    requiredLink: equipmentType.requiredLink,
    unitId: null,
  };
}

function serializeEquipmentVersionForNetwork(
  equipmentVersion: ProcedureEquipmentVersion
): ProcedureEquipmentVersionREST {
  const {id, cid, name, description, clonedFromId, models, procedureEquipmentId} = equipmentVersion;
  return {
    id,
    cid,
    name,
    description,
    clonedFromId,
    allowedEquipmentModels: models,
    procedureEquipmentId,
    dataType: serializeEquipmentTypeForNetwork(equipmentVersion.dataType),
  };
}

function serializeDataParameterTypeForNetwork(dataParameterType: DataParameterType): DataParameterTypeREST {
  return {
    type: dataParameterType.type,
    requiredLink: dataParameterType.requiredLink,
    unitId: dataParameterType.unit ? dataParameterType.unit.id : null,
  };
}

function serializeDataParameterVersionForNetwork(
  dataParameterVersion: ProcedureDataParameterVersion
): ProcedureDataParameterVersionREST {
  const {
    id,
    cid,
    name,
    description,
    clonedFromId,
    minimum,
    maximum,
    procedureDataParameterId,
    confirmationMethod,
    libraryParameterDefinition,
  } = dataParameterVersion;
  return {
    id,
    cid,
    name,
    description,
    clonedFromId,
    minimum,
    maximum,
    procedureDataParameterId,
    confirmationMethod,
    libraryParameterDefinitionId: libraryParameterDefinition?.id ?? null,
    dataType: serializeDataParameterTypeForNetwork(dataParameterVersion.dataType),
  };
}

function serializeParameterPlannedValueForNetwork(
  parameterPlannedValue: ProcedureDataParameterPlannedValue
): ProcedureDataParameterPlannedValueREST {
  return {
    id: parameterPlannedValue.id,
    cid: parameterPlannedValue.cid,
    value: parameterPlannedValue.value,
  };
}

function serializeMaterialInputPlannedValueForNetwork(
  materialInputPlannedValue: ProcedureMaterialInputPlannedValue
): ProcedureMaterialInputPlannedValueREST {
  return {
    id: materialInputPlannedValue.id,
    cid: materialInputPlannedValue.cid,
    material: materialInputPlannedValue.material,
    amount: materialInputPlannedValue.amount,
  };
}

function serializeEquipmentPlannedValueForNetwork(
  equipmentPlannedValue: ProcedureEquipmentPlannedValue
): ProcedureEquipmentPlannedValueREST {
  return {
    id: equipmentPlannedValue.id,
    cid: equipmentPlannedValue.cid,
    equipment: equipmentPlannedValue.equipment,
  };
}

function serializeParameterSettingForNetwork(
  dataParameterSetting: ProcedureDataParameterSetting
): ProcedureDataParameterSettingREST {
  return {
    dataParameterVersion: serializeDataParameterVersionForNetwork(dataParameterSetting.dataParameterVersion),
    values: dataParameterSetting.values.map((v) => serializeParameterPlannedValueForNetwork(v)),
  };
}

function serializeMaterialInputSettingForNetwork(
  materialInputSetting: ProcedureMaterialInputSetting
): ProcedureMaterialInputSettingREST {
  return {
    materialInputVersion: serializeMaterialInputVersionForNetwork(materialInputSetting.materialInputVersion),
    values: materialInputSetting.values.map((v) => serializeMaterialInputPlannedValueForNetwork(v)),
  };
}

function serializeMaterialOutputSettingForNetwork(
  materialOutputSetting: ProcedureMaterialOutputSetting
): ProcedureMaterialOutputSettingREST {
  return {
    materialOutputVersion: serializeMaterialOutputVersionForNetwork(
      materialOutputSetting.materialOutputVersion
    ),
  };
}

function serializeEquipmentSettingForNetwork(
  equipmentSetting: ProcedureEquipmentSetting
): ProcedureEquipmentSettingREST {
  return {
    equipmentVersion: serializeEquipmentVersionForNetwork(equipmentSetting.equipmentVersion),
    values: equipmentSetting.values.map((v) => serializeEquipmentPlannedValueForNetwork(v)),
  };
}

function serializeAnalysisTemplateConfigurationsForNetwork(
  configs: Array<ProcedureAnalysisTemplateConfiguration | InaccessibleProcedureAnalysisTemplateConfiguration>
): Array<ProcedureAnalysisTemplateConfigurationREST> {
  return configs
    .filter(
      (config): config is ProcedureAnalysisTemplateConfiguration =>
        !isInaccessibleAnalysisTemplateConfiguration(config)
    )
    .map(({analysisTemplateId, config}) => ({
      analysisTemplateId,
      configJson: config,
    }));
}

function serializeTestDefinitionsForNetwork(
  testDefinitions: Array<ProcedureTestDefinition | InaccessibleProcedureTestDefinition>
): Array<ProcedureTestDefinitionREST> {
  return testDefinitions
    .filter(
      (testDef): testDef is ProcedureTestDefinition =>
        !isInaccessibleProcedureTestDefinition(testDef) && testDef.id != null
    )
    .map((testDef) => ({id: testDef.id!}));
}

export function serializeMethodNodeForNetwork(
  methodNode: MethodNode,
  persistenceInfo?: PersistenceInfo,
  processFlowchartConfigId?: string
): MethodNodeREST {
  const {
    id,
    cid,
    name,
    description,
    sourceMethodId,
    nodeId,
    provenanceId,
    analysisTemplateConfigurations,
    testDefinitions,
  } = methodNode;
  return {
    id,
    cid,
    name,
    description,
    processFlowchartConfigId: processFlowchartConfigId ?? null,
    stepGroups:
      methodNode.stepGroups == null
        ? []
        : methodNode.stepGroups.map((sg) => serializeProcedureStepGroupForNetwork(sg, persistenceInfo)),
    dataParameterSettings: methodNode.dataParameterSettings.map((ps) =>
      serializeParameterSettingForNetwork(ps)
    ),
    materialInputSettings: methodNode.materialInputSettings.map((mis) =>
      serializeMaterialInputSettingForNetwork(mis)
    ),
    materialOutputSettings: methodNode.materialOutputSettings.map((mis) =>
      serializeMaterialOutputSettingForNetwork(mis)
    ),
    equipmentSettings: methodNode.equipmentSettings.map((es) => serializeEquipmentSettingForNetwork(es)),
    sourceMethodId,
    nodeId,
    provenanceId,
    gridLayoutCoordinates: methodNode.canvasGridCoordinates,
    analysisTemplateConfigurations: serializeAnalysisTemplateConfigurationsForNetwork(
      analysisTemplateConfigurations
    ),
    testDefinitions: serializeTestDefinitionsForNetwork(testDefinitions),
    dilutionPlan: methodNode.dilutionPlan,
  };
}

function serializeProcedureEdgeForNetwork(edge: ProcedureEdge): ProcedureEdgeREST {
  const {type, fromMethodCid: fromMethod, toMethodCid: toMethod, sourceHandle, targetHandle} = edge;
  switch (type) {
    case ProcedureEdgeType.METHOD_EDGE:
      return {
        type,
        sourceId: fromMethod,
        targetId: toMethod,
      };
    case ProcedureEdgeType.MATERIAL_EDGE:
      return {
        type,
        sourceId: getMaterialCidFromHandle(sourceHandle),
        targetId: getMaterialCidFromHandle(targetHandle),
      };
    default:
      assert(false, 'unsupported edge type!');
  }
}

/**
 * At the last moment, as we prepare the data for the API, we fill in empty node names. This ensures that
 * the server always has useful and unique names (particularly for crafting audit events).
 *
 * This state is designed to be brief. A truly empty node name should only exist on the front end while
 * the node name editor is open on a new node. All other scenarios should be blocked by application code.
 *
 * Note that this will cause the server flowchart to diverge from what the user sees, if only briefly.
 * It will also cause infinite autosave loops during this time, so it's recommended to disconnect
 * the autosaver while the node name editor is open.
 */
function fillEmptyNodeNames(nodes: Array<MethodNode>, procedureType: ProcedureType): Array<MethodNode> {
  const allNodeNames = nodes.map((n) => n.name);
  return nodes.map((n) =>
    n.name.length === 0
      ? {...n, name: normalizeNameForUniqueness('', allNodeNames, getMethodNamePlaceholder(procedureType))}
      : n
  );
}

/**
 * Serializes a ProcedureFlowchart to a ProcedureFlowchartREST object for network transmission.
 *
 * @param lastSavedFlowchartVersionId The flowchart version ID of the last saved flowchart.
 *  This is used to determine if the frontend state is stale compared to the latest backend state for this procedure.
 */
export default function serializeProcedureFlowchartForNetwork(
  flowchart: ProcedureFlowchart,
  procedureType: ProcedureType,
  persistenceInfo?: PersistenceInfo,
  lastSavedFlowchartVersionId?: string
): ProcedureFlowchartREST {
  const {nodes, edges} = flowchart;
  return {
    nodes: fillEmptyNodeNames(nodes, procedureType).map((n) =>
      serializeMethodNodeForNetwork(n, persistenceInfo)
    ),
    edges: edges.map((e) => serializeProcedureEdgeForNetwork(e)),
    // Naming mismatch intentional, verbiage for frontend and backend is specific for those contexts
    latestFlowchartVersionId: lastSavedFlowchartVersionId,
  };
}
